"use client";
import { useState, useCallback, useEffect, memo, useContext } from "react";
import { SubmitFormAPI } from "@/util/services/submit-form/submitForm.service";
import DynamicFormThankYou from "./dynamic-form-thank-you";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { Loader2, Trash2 } from "lucide-react";
import TrackingContext from "@/lib/providers/tracking-provider";
import { CallAPI_GOOGLE_Verify } from "@/util/services/google/api";
import dynamic from "next/dynamic";
const DatePickerComponent = dynamic(() => import("../date-picker"), { ssr: false });
import ColorStyleContext from "@/lib/providers/color-style-privider";
import { useSession } from "next-auth/react";

const DynamicFormClient = ({
    dynamicForm,
    recaptcha,
    text,
    score,
    defaultLanguage,
}: {
    dynamicForm: any;
    recaptcha: boolean;
    text: any;
    score: number;
    defaultLanguage: string;
}) => {
    const { referrer, landingUrl, utmSource, utmMedium, utmCampaign, gclid, fbclid } = useContext(TrackingContext);

    // สร้างค่าเริ่มต้นให้กับ formData
    const initialFormData = dynamicForm.fields.reduce((acc: any, field: any) => {
        switch (field.type) {
            case "text":
            case "textarea":
            case "email":
            case "tel":
            case "fullname":
            case "radio":
                acc[field.key] = ""; // ค่าเริ่มต้นเป็น string ว่าง
                break;
            case "number":
                acc[field.key] = 0; // ค่าเริ่มต้นเป็นเลข 0
                break;
            case "checkbox":
                acc[field.key] = []; // ค่าเริ่มต้นเป็นอาร์เรย์ว่าง
                break;
            case "upload":
                acc[field.key] = []; // ค่าเริ่มต้นเป็นอาร์เรย์ว่าง
                acc[`${field.key}_File`] = []; // สร้าง key สำหรับเก็บไฟล์
                break;
            case "date":
                acc[field.key] = null; // ค่าเริ่มต้นเป็น null สำหรับ date
                break;
            case "consent":
                if (field.consentType === "radio") {
                    acc[field.key] = ""; // ค่าเริ่มต้นเป็น string ว่าง (กรณี radio consent)
                } else {
                    if (defaultLanguage === "th") {
                        acc[field.key] = "ยินยอม";
                    } else {
                        acc[field.key] = "Consent";
                    }
                }
                break;
            case "select":
                acc[field.key] = ""; // ค่าเริ่มต้นเป็น string ว่าง
                break;
            default:
                break;
        }
        return acc;
    }, {});

    const [formData, setFormData] = useState<{ [key: string]: any }>(initialFormData);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isDone, setIsDone] = useState(false);

    const validateForm = (formData: any, fields: any) => {
        const errors: Record<string, string> = {};

        fields.forEach((field: any) => {
            const value = formData[field.key];
            if (field.required) {
                switch (field.type) {
                    case "text":
                    case "textarea":
                    case "fullname":
                        if (!value || value.trim() === "") {
                            errors[field.key] = text["validate_required"];
                        }
                        break;
                    case "email":
                        if (!value || value.trim() === "") {
                            errors[field.key] = text["validate_required"];
                        } else if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                            errors[field.key] = text["validate_email"];
                        }
                        break;
                    case "number":
                        if (!value || value.trim() === "") {
                            errors[field.key] = text["validate_required"];
                        } else if (!value || isNaN(Number(value))) {
                            errors[field.key] = text["validate_number"];
                        }
                        break;
                    case "checkbox":
                        if (!Array.isArray(value) || value.length === 0) {
                            errors[field.key] = text["validate_checkbox"];
                        }
                        break;
                    case "radio":
                        if (!value) {
                            errors[field.key] = text["validate_radio"];
                        }
                        break;
                    case "upload":
                        if (!Array.isArray(value) || value.length === 0) {
                            errors[field.key] = text["validate_upload"];
                        }
                        break;
                    case "date":
                        if (!value || isNaN(Date.parse(value))) {
                            errors[field.key] = text["validate_required"];
                        }
                        break;
                    case "tel":
                        if (!value || value.trim() === "") {
                            errors[field.key] = text["validate_required"];
                        } else if (!value || !/^(\+?[1-9]{1}[0-9]{1,14}|0[0-9]{9})$/.test(value)) {
                            errors[field.key] = text["validate_tel"];
                        }
                        break;
                    case "select":
                        if (!value) {
                            errors[field.key] = text["validate_select"];
                        }
                        break;
                    case "consent":
                        if (field.consentType === "radio" && !value) {
                            errors[field.key] = text["validate_select"];
                        }
                        break;
                    default:
                        break;
                }
            } else {
                switch (field.type) {
                    case "email":
                        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                            errors[field.key] = text["validate_email"];
                        }
                        break;
                    case "number":
                        if (value && isNaN(Number(value))) {
                            errors[field.key] = text["validate_number"];
                        }
                        break;
                    case "tel":
                        if (value && !/^(\+?[1-9]{1}[0-9]{1,14}|0[0-9]{9})$/.test(value)) {
                            errors[field.key] = text["validate_tel"];
                        }
                        break;
                    default:
                        break;
                }
            }
        });

        return errors;
    };

    // ฟังก์ชันสำหรับเปลี่ยนค่าฟอร์ม
    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
            const { name, value, type } = e.target;
            const checked = (e.target as HTMLInputElement).checked;

            setFormData((prevData) => {
                const newFormData = { ...prevData };
                if (type === "checkbox") {
                    const prevValues = prevData[name] || [];
                    newFormData[name] = checked
                        ? [...prevValues, value]
                        : prevValues.filter((v: string) => v !== value);
                } else {
                    newFormData[name] = value;
                }

                // ลบ error เมื่อผู้ใช้แก้ไข
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    [name]: "", // ลบ error สำหรับ field นี้
                }));

                return newFormData;
            });
        },
        [],
    );

    // ฟังก์ชันสำหรับจัดการเลือกวันที่
    const handleDateChange = useCallback((name: string, newValue: any) => {
        const formattedDate = newValue && newValue.isValid() ? newValue.format("YYYY-MM-DD") : null;
        setFormData((prevData) => ({
            ...prevData,
            [name]: formattedDate,
        }));

        // ลบ error เมื่อผู้ใช้เลือกวันที่
        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: "", // ลบ error สำหรับ field นี้
        }));
    }, []);
    // ฟังก์ชันสำหรับ submit
    const { executeRecaptcha } = useGoogleReCaptcha();
    const session = useSession();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitted(true);
        try {
            // ถ้า recaptcha เป็น true ให้ตรวจสอบผ่าน reCAPTCHA
            if (recaptcha) {
                if (!executeRecaptcha) {
                    console.error("Google reCAPTCHA not available");
                    throw new Error("reCAPTCHA is not initialized");
                }

                const token = await executeRecaptcha("contact_form");
                if (!token) {
                    throw new Error("reCAPTCHA verification failed");
                }
                const checkBot = await CallAPI_GOOGLE_Verify({ token: token });

                if (checkBot.success !== true) {
                    throw new Error("reCAPTCHA verification failed");
                }

                if (checkBot.score < score) {
                    throw new Error("reCAPTCHA verification failed");
                }
            }

            const errors = validateForm(formData, dynamicForm.fields);
            if (Object.keys(errors).length > 0) {
                setErrors(errors);
                setIsSubmitted(false);
                return;
            } else {
                setErrors({});
            }

            const enrichedFormData = {
                referrer: referrer || "",
                landingUrl: landingUrl || "",
                submitUrl: window.location.href,
                utmSource: utmSource || "",
                utmMedium: utmMedium || "",
                utmCampaign: utmCampaign || "",
                gclid: gclid || "",
                fbclid: fbclid || "",
            };

            // ส่งข้อมูลฟอร์มไปยัง API
            const result = await SubmitFormAPI(dynamicForm.id, formData, enrichedFormData, session.data?.user?.id);
            if (result.statusCode === 200) {
                setIsDone(true);
            }
        } catch (error: any) {
            if (error.name === "ValidationError") {
                // Validation Errors
                const errorMessages: { [key: string]: string } = {};
                error.inner.forEach((validationError: any) => {
                    errorMessages[validationError.path] = validationError.message;
                });
                setErrors(errorMessages);
                setIsSubmitted(false);
            } else {
                // Other Errors เช่น reCAPTCHA หรือ API
                console.error("An error occurred:", error.message);
                alert(`Error: ${error.message}`);
                setIsSubmitted(false);
            }
        }
    };

    let wrapperClass = "flex justify-center w-full";
    let thankYouClass = "text-center min-h-52 content-center";
    if (dynamicForm.formType === "PRODUCT_CONTACT_FORM") {
        wrapperClass = "flex justify-start w-full";
        thankYouClass = "w-full min-h-52";
    }

    return (
        <>
            {!isDone ? (
                <form onSubmit={handleSubmit}>
                    <div className={wrapperClass}>
                        <div className="w-full max-w-[600px]">
                            {dynamicForm.fields.map((field: any) => (
                                <div className="py-3" key={field.id}>
                                    <FieldRenderer
                                        errors={errors}
                                        field={field}
                                        handleInputChange={handleInputChange}
                                        handleDateChange={handleDateChange}
                                        formData={formData}
                                        setErrors={setErrors}
                                        setFormData={setFormData} // เพิ่มการส่ง setFormData
                                        dynamicForm={dynamicForm}
                                        isSubmitted={isSubmitted}
                                        text={text}
                                        defaultLanguage={defaultLanguage}
                                    />
                                    {errors[field.key] && (
                                        <p className="text-red-500 text-sm mt-1">{errors[field.key]}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </form>
            ) : (
                <div className={thankYouClass}>
                    <DynamicFormThankYou text={text} />
                </div>
            )}
        </>
    );
};

// ใช้ memo ลดการ rerender
export const FieldRenderer = memo(
    ({
        field,
        errors,
        handleInputChange,
        handleDateChange,
        formData,
        setErrors,
        setFormData,
        dynamicForm,
        isSubmitted,
        text,
        defaultLanguage,
    }: {
        field: any;
        errors: { [key: string]: string };
        handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
        handleDateChange: (name: string, newValue: any) => void;
        formData: { [key: string]: any };
        setErrors: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
        setFormData: React.Dispatch<React.SetStateAction<{ [key: string]: any }>>; // เพิ่ม type ของ setFormData
        dynamicForm: any;
        isSubmitted: boolean;
        text: any;
        defaultLanguage?: string;
    }) => {
        const [optionsToDisplay, setOptionsToDisplay] = useState<any[]>([]);
        const colorStyle = useContext(ColorStyleContext);
        const isProductForm = dynamicForm?.formType === "PRODUCT_CONTACT_FORM";

        useEffect(() => {
            if (field.type === "select") {
                if (field.isChild && field.parentField) {
                    const parentValue = formData[field.parentField];
                    const linkedOptions = JSON.parse(field.linkedOptions || {});
                    setOptionsToDisplay(linkedOptions[parentValue] || []);
                } else {
                    setOptionsToDisplay(JSON.parse(field.options || []));
                }
            }
        }, [field, formData]);

        switch (field.type) {
            case "header":
                return (
                    <div
                        className={field.type === "header" ? "text-3xl font-semibold text-center" : ""}
                        style={{ color: colorStyle.headingMain || "#254b7c" }}>
                        <div dangerouslySetInnerHTML={{ __html: field.label }} />
                    </div>
                );
            case "paragraph":
                return (
                    <div
                        className={field.type === "header" ? "text-3xl font-semibold text-center" : ""}
                        style={{ color: colorStyle.headingSub || "#2c568a" }}>
                        <div dangerouslySetInnerHTML={{ __html: field.label }} />
                    </div>
                );

            case "privacy":
                return <div className="text-sm text-gray-500" dangerouslySetInnerHTML={{ __html: field.label }} />;
            case "checkbox":
                const checkboxOptions = Array.isArray(field.options)
                    ? field.options
                    : JSON.parse(field.options || "[]");

                return (
                    <div>
                        <label htmlFor={field.key} className="block mb-1 text-base font-normal">
                            <p style={{ color: colorStyle.text || "#3d3d3d" }}>
                                {field.label} {field.required && <span className="text-red-500">*</span>}
                            </p>
                        </label>
                        {checkboxOptions.map((option: any) => (
                            <div
                                key={option.key}
                                className="flex items-center space-x-2"
                                style={{ color: colorStyle.text || "#3d3d3d" }}>
                                <input
                                    type="checkbox"
                                    id={option.key}
                                    name={field.key}
                                    value={option.key} // กำหนด value เป็น key
                                    onChange={handleInputChange}
                                    className="form-check-input"
                                />
                                <label
                                    htmlFor={option.key}
                                    className={`text-sm
                                    ${errors[field.key] ? "text-red-500" : ""}
                                    `}>
                                    {option.label}
                                </label>
                            </div>
                        ))}
                    </div>
                );

            case "radio":
                const radioOptions = Array.isArray(field.options) ? field.options : JSON.parse(field.options || "[]");

                return (
                    <div>
                        <label htmlFor={field.key} className="block mb-1 text-base font-normal">
                            <p style={{ color: colorStyle.text || "#3d3d3d" }}>
                                {field.label} {field.required && <span className="text-red-500">*</span>}
                            </p>
                        </label>
                        {radioOptions.map((option: any) => (
                            <div
                                key={option.key}
                                className="flex items-center space-x-2"
                                style={{ color: colorStyle.text || "#3d3d3d" }}>
                                <input
                                    type="radio"
                                    id={option.key}
                                    name={field.key}
                                    value={option.key}
                                    onChange={handleInputChange}
                                    className="form-check-input"
                                />
                                <label
                                    htmlFor={option.key}
                                    className={`text-sm
                                    ${errors[field.key] ? "text-red-500" : ""}
                                    `}>
                                    {option.label}
                                </label>
                            </div>
                        ))}
                    </div>
                );
            case "select":
                return (
                    <div>
                        <label htmlFor={field.key} className="block mb-1 text-base font-normal">
                            <p style={{ color: colorStyle.text || "#3d3d3d" }}>
                                {field.label} {field.required && <span className="text-red-500">*</span>}
                            </p>
                        </label>
                        <select
                            id={field.key}
                            name={field.key}
                            value={formData[field.key] || ""}
                            onChange={handleInputChange}
                            disabled={field.isChild && field.parentField && optionsToDisplay.length === 0}
                            className={`form-control w-full p-2 border rounded bg-white ${
                                errors[field.key] ? "border-red-500" : "border-gray-300"
                            } ${
                                field.isChild && field.parentField && optionsToDisplay.length === 0
                                    ? "opacity-60 cursor-not-allowed"
                                    : ""
                            }`}
                            style={{ color: colorStyle.text || "#3d3d3d" }}>
                            <option value="" disabled hidden>
                                {field.isChild && field.parentField && optionsToDisplay.length === 0
                                    ? `${text["parent_select_in"]} ${
                                          dynamicForm.fields.find((f: any) => f.key === field.parentField)?.label ||
                                          text["parent_select"]
                                      }`
                                    : text["please_select"]}
                            </option>
                            {optionsToDisplay.map((option: any, idx: number) => (
                                <option key={option.key} value={option.key}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                );

            case "textarea":
                return (
                    <div>
                        <label htmlFor={field.key} className="block mb-1 text-base font-normal">
                            <p style={{ color: colorStyle.text || "#3d3d3d" }}>
                                {field.label} {field.required && <span className="text-red-500">*</span>}
                            </p>
                        </label>
                        <textarea
                            id={field.key}
                            name={field.key}
                            onChange={handleInputChange}
                            className={`form-control w-full p-2 border rounded min-h-[10rem]
                                ${errors[field.key] ? "border-red-500" : "border-gray-300"}`}
                            style={{ color: colorStyle.text || "#3d3d3d" }}
                            placeholder={field.placeholder}
                        />
                    </div>
                );

            case "date":
                return (
                    <div className="min-h-11">
                        <label htmlFor={field.key} className="block mb-1 text-base font-normal">
                            <p style={{ color: colorStyle.text || "#3d3d3d" }}>
                                {field.label} {field.required && <span className="text-red-500">*</span>}
                            </p>
                        </label>
                        <DatePickerComponent
                            id={field.key}
                            label={field.label}
                            required={field.required}
                            value={formData[field.key]} // ค่าเริ่มต้น
                            onChange={(newValue) => handleDateChange(field.key, newValue)} // ฟังก์ชันจัดการการเปลี่ยนแปลง
                            hasError={!!errors[field.key]} // ตรวจสอบข้อผิดพลาด
                            defaultLanguage={defaultLanguage}
                        />
                    </div>
                );

            case "upload":
                const fileAccept = field?.fileAccept || [
                    "image/png",
                    "image/jpeg",
                    "image/webp",
                    "image/avif",
                    "application/msword",
                    "application/vnd.ms-excel",
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                    "application/pdf",
                ];

                const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
                    const files = Array.from(event.target.files || []);
                    const acceptedFiles = files.filter((file) => fileAccept.includes(file.type));
                    if (acceptedFiles.length !== files.length) {
                        setErrors((prevErrors) => ({
                            ...prevErrors,
                            [field.key]: text["invalid_file_type"],
                        }));
                        return;
                    }
                    // check size of each file < 5 MB
                    const invalidSizeFiles = acceptedFiles.filter((file) => file.size > 5 * 1024 * 1024);
                    if (invalidSizeFiles.length > 0) {
                        setErrors((prevErrors) => ({
                            ...prevErrors,
                            [field.key]: text["large_file_size"],
                        }));
                        return;
                    }

                    let newFiles: File[] = [];
                    let newFileNames: { path: string; relativePath: string }[] = []; // ระบุประเภทให้ชัดเจน

                    if (field.multipleFile) {
                        // กรณีเลือกได้หลายไฟล์ ให้รวมไฟล์ใหม่กับไฟล์เดิม
                        const existingFiles: File[] = formData[`${field.key}_File`] || [];
                        const existingFileNames: { path: string; relativePath: string }[] = formData[field.key] || [];

                        newFiles = [...existingFiles, ...acceptedFiles].slice(0, field.limitFile || Infinity);
                        newFileNames = [
                            ...existingFileNames,
                            ...acceptedFiles.map((file) => ({
                                path: file.name,
                                relativePath: file.name,
                            })),
                        ].slice(0, field.limitFile || Infinity);
                    } else {
                        newFiles = [...(formData[`${field.key}_File`] || []), ...acceptedFiles].slice(
                            0,
                            field.limitFile || 1,
                        );
                        newFileNames = [
                            ...(formData[field.key] || []),
                            ...acceptedFiles.map((file) => ({
                                path: file.name,
                                relativePath: file.name,
                            })),
                        ].slice(0, field.limitFile || 1);
                    }

                    setFormData((prevData) => ({
                        ...prevData,
                        [field.key]: newFileNames,
                        [`${field.key}_File`]: newFiles,
                    }));

                    setErrors((prevErrors) => ({
                        ...prevErrors,
                        [field.key]: "",
                    }));

                    event.target.value = "";
                };

                const handleRemoveFile = (e: React.MouseEvent, index: number) => {
                    e.stopPropagation();
                    setFormData((prevData) => ({
                        ...prevData,
                        [field.key]: prevData[field.key].filter((_: any, i: number) => i !== index),
                        [`${field.key}_File`]: prevData[`${field.key}_File`].filter(
                            (_: File, i: number) => i !== index,
                        ),
                    }));
                };

                return (
                    <div>
                        <label htmlFor={field.key} className="block mb-1 text-base font-normal">
                            <p style={{ color: colorStyle.text || "#3d3d3d" }}>
                                {field.label} {field.required && <span className="text-red-500">*</span>}
                            </p>
                        </label>
                        <div
                            className="w-full border rounded border-gray-300"
                            style={{
                                border: "2px dashed #ccc",
                                borderRadius: "8px",
                                padding: "16px",
                                textAlign: "center",
                                backgroundColor: "#fafafa",
                                cursor:
                                    formData[field.key]?.length >= (field.limitFile || Infinity)
                                        ? "not-allowed"
                                        : "pointer",
                            }}
                            onClick={() => {
                                if (formData[field.key]?.length < (field.limitFile || Infinity)) {
                                    document.getElementById(field.key)?.click();
                                }
                            }} // คลิกเปิด popup
                        >
                            {formData[field.key]?.length < (field.limitFile || Infinity) && (
                                <label
                                    className="cursor-pointer dynamic-color"
                                    style={
                                        {
                                            "--dynamic-color": colorStyle.text || "#3d3d3d",
                                            "--dynamic-hover-color": colorStyle.linkHover || "#254b7c",
                                            "--dynamic-focus-color": colorStyle.linkHover || "#254b7c",
                                        } as React.CSSProperties
                                    }>
                                    {`${text["drag_drop_files"]} (${text["max_file"]}: ${
                                        field.limitFile || "Unlimited"
                                    })`}
                                </label>
                            )}
                            <input
                                id={field.key}
                                type="file"
                                accept={fileAccept.join(",")}
                                onChange={handleFileChange}
                                multiple={field.multipleFile}
                                className="hidden"
                            />
                            {(formData[field.key]?.length > 0 || formData[`${field.key}_File`]?.length > 0) && (
                                <div className="pt-2">
                                    {formData[field.key].map(
                                        (file: { path: string; relativePath: string }, index: number) => (
                                            <div key={index} className="flex items-center space-x-2">
                                                <div
                                                    style={{ flexGrow: 1, color: colorStyle.text || "#3d3d3d" }}
                                                    className="text-left w-13/14 break-all">
                                                    <p>{file.path}</p>
                                                    <small className="text-grayc2">
                                                        Size:{" "}
                                                        {(
                                                            formData[`${field.key}_File`]?.[index]?.size /
                                                                (1024 * 1024) || 0
                                                        ).toFixed(2)}{" "}
                                                        MB
                                                    </small>
                                                </div>
                                                <div className="w-1/14 flex justify-end">
                                                    <button
                                                        className="text-red-500 hover:text-red-700 transition-colors"
                                                        onClick={(e) => handleRemoveFile(e, index)}>
                                                        <Trash2 />
                                                    </button>
                                                </div>
                                            </div>
                                        ),
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                );

            case "consent":
                const options = Array.isArray(field.options) ? field.options : JSON.parse(field.options || "[]"); // แปลง JSON string เป็น array หากจำเป็น

                return (
                    <div>
                        <label
                            className={`block mb-2 text-base ${
                                field.consentType !== "radio" ? (isProductForm ? "" : "text-center") : ""
                            }`}>
                            <p style={{ color: colorStyle.text || "#3d3d3d" }}>
                                {field.label}{" "}
                                {field.required && field.consentType === "radio" && (
                                    <span className="text-red-500">*</span>
                                )}
                            </p>
                        </label>
                        {field.consentType === "radio" && (
                            <div>
                                {options.map((option: any) => (
                                    <div key={option.key} className="flex items-center mb-2 space-x-2">
                                        <input
                                            type="radio"
                                            id={option.key}
                                            name={field.key}
                                            value={option.key}
                                            className="form-radio"
                                            onChange={handleInputChange} // ใช้ handleInputChange
                                        />
                                        <label
                                            htmlFor={`${option.key}`}
                                            className={`text-sm
                                                ${errors[field.key] ? "text-red-500" : ""}
                                            `}
                                            style={{ color: colorStyle.text || "#3d3d3d" }}>
                                            {option.label}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );

            case "submit":
                return (
                    <div className={`${isProductForm ? "" : "text-center"}`}>
                        <button
                            disabled={isSubmitted} 
                            className={`max-w-[260px] h-14 text-white font-normal py-2 px-4 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75 text-lg w-full dynamic-bg dynamic-color
                            ${isSubmitted ? "cursor-not-allowed bg-gray-400" : ""} dynamic-bg dynamic-color
                            `}
                            style={
                                {
                                    "--dynamic-bg": colorStyle.buttonBgNormal || "#009eca",
                                    "--dynamic-hover-bg": colorStyle.buttonBgHover || "#254b7c",
                                    "--dynamic-color": colorStyle.buttonTextNormal || "#ffffff",
                                    "--dynamic-hover-color": colorStyle.buttonTextHover || "#ffffff",
                                    "--dynamic-focus-color": colorStyle.buttonTextHover || "#ffffff",
                                } as React.CSSProperties
                            }>
                            <span className="flex justify-center">
                                {isSubmitted ? <Loader2 className="w-6 h-6 animate-spin" /> : field.label}
                            </span>
                        </button>
                    </div>
                );

            case "number":
                return (
                    <div>
                        <label htmlFor={field.key} className="block mb-1 text-base font-normal">
                            <p style={{ color: colorStyle.text || "#3d3d3d" }}>
                                {field.label} {field.required && <span className="text-red-500">*</span>}
                            </p>
                        </label>
                        <input
                            type="number"
                            value={formData[field.key]}
                            id={field.key}
                            name={field.key}
                            onChange={handleInputChange}
                            className={`form-control w-full h-[40px] border p-2 rounded pl-2 ${
                                errors[field.key] ? "border-red-500" : "border-gray-300"
                            }`}
                            style={{ color: colorStyle.text || "#3d3d3d" }}
                        />
                    </div>
                );
            case "email":
            case "fullname":
                return (
                    <div>
                        <label htmlFor={field.key} className="block mb-1 text-base font-normal">
                            <p style={{ color: colorStyle.text || "#3d3d3d" }}>
                                {field.label} {field.required && <span className="text-red-500">*</span>}
                            </p>
                        </label>
                        <input
                            type="text"
                            id={field.key}
                            name={field.key}
                            onChange={handleInputChange}
                            className={`form-control w-full h-[40px] border p-2 rounded pl-2 ${
                                errors[field.key] ? "border-red-500" : "border-gray-300"
                            }`}
                            placeholder={field.placeholder}
                        />
                    </div>
                );
            default:
                return (
                    <div>
                        <label htmlFor={field.key} className="block mb-1 text-base font-normal">
                            <p style={{ color: colorStyle.text || "#3d3d3d" }}>
                                {field.label} {field.required && <span className="text-red-500">*</span>}
                            </p>
                        </label>
                        <input
                            type={field.type}
                            id={field.key}
                            name={field.key}
                            onChange={handleInputChange}
                            className={`form-control w-full h-[40px] border p-2 rounded pl-2 ${
                                errors[field.key] ? "border-red-500" : "border-gray-300"
                            }`}
                            style={{ color: colorStyle.text || "#3d3d3d" }}
                            placeholder={field.placeholder}
                        />
                    </div>
                );
        }
    },
);

FieldRenderer.displayName = "FieldRenderer";

export default DynamicFormClient;
